import {
  ActivePanelManager,
  callInSequence,
  Checkbox,
  Theme,
  Toasts,
  useCallbackRef,
} from '@infotech-soe/erp-ui-kit';
import classNames from 'classnames';
import { debounce } from 'lodash';
import React, {
  ChangeEvent,
  DragEvent,
  forwardRef,
  HTMLAttributes,
  memo,
  MouseEvent,
  Ref,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useTheme } from 'react-jss';
import {
  AutoSizer,
  GridCellProps,
  MultiGrid,
  MultiGridProps,
} from 'react-virtualized';
import 'react-virtualized/styles.css';

import {
  $ROW_HIDDEN_LABEL,
  CELL_ID_PREFIX,
  CHECKBOX_COLUMN_WIDTH,
  DATASET_ID_PREFIX,
  DEFAULT_COLUMN_WIDTH,
  FEATURES,
  GRID_MODE,
  MIN_COLUMN_WIDTH,
  ROW_HEIGHT,
  SEARCH_DIRECTIONS,
  SEARCH_INPUT_MIN_WIDTH,
} from '../../../constants/dataset';
import {
  DatasetColumnFormat,
  DatasetGridColumn,
} from '../../../services/dataset/view-model/DatasetGridColumn';
import { DatasetGridRow } from '../../../services/dataset/view-model/DatasetGridRow';
import { useContextMenu } from '../../hooks/useContextMenu';
import { getCoordsFromElementOrEvent } from '../../utils/getCoordsFromElementOrEvent';

import CellInput, {
  CellInputSubmitHandler,
  GRID_CELL_INPUT_CLASS,
} from './components/CellInput';
import DatasetConfigForm from './components/ConfigForm';
import DatasetExportController from './components/DatasetExportController';
import FilterPopover from './components/FilterPopover';
import GridContextMenu from './components/GridContextMenu';
import GridHeader, { SortChangeHandler } from './components/HeaderCell';
import SearchHighlighter from './components/SearchHighlighter';
import SearchInput from './components/SearchInput';
import { DatasetGridContainerProps } from './DatasetGridContainer';
import { useDatasetSearch, useHeaderDragAutoScroll, useKeyDown } from './hooks';
import useStyles from './styles';
import { applyOddEvenRowStyles } from './utils/applyOddEvenRowStyles';
import { copyTextToClipboard } from './utils/copyTextToClipboard';
import {
  getBorderClassNames,
  getClassNamesFromMap,
  TBorderSide,
} from './utils/getBorderClassNames';
import { getCommand } from './utils/getCommand';
import {
  getFormattedActiveCellValue,
  getFormattedValue,
  IntlFormatter,
} from './utils/getFormattedValue';
import { getNextCellIndex } from './utils/getNextCellIndex';
import { getOrderedColumns } from './utils/getOrderedColumns';
import { isEven } from './utils/isEven';
import { getCellStyles, getRowStyles } from './utils/stylingHandler';

const messages = defineMessages({
  datasetConfigFormTitle: {
    id: 'core.category.grid.config.title',
    defaultMessage: 'Preferences',
  },
  copied: {
    id: 'core.errorNotification.copied',
    defaultMessage: 'Copied!',
  },
});

export interface DatasetGridProps
  extends
    DatasetGridContainerProps,
    Pick<HTMLAttributes<HTMLDivElement>, 'className'> {
  disabled?: boolean;
  labelTitle?: string;
  totals?: number;
  withBorders?: TBorderSide | TBorderSide[];
  onChange?: (value: DatasetGridRow[]) => void;
  onChanged?: (value: DatasetGridRow[]) => void;
}

export type DatasetGridSearchHandler = (
  event: KeyboardEvent | ChangeEvent<HTMLInputElement>,
  searchRows?: DatasetGridRow[],
  direction?: SEARCH_DIRECTIONS
) => void;

const DatasetGrid = (
  props: DatasetGridProps,
  ref: Ref<HTMLDivElement>
): React.JSX.Element => {
  const intl = useIntl();
  const theme = useTheme<Theme>();
  const {
    activeRowId,
    activeCategoryId,
    categoryId,
    className,
    columns,
    commands,
    conditions,
    conditionsCount,
    hasConditionsHook,
    config,
    datasetId: datasetIdProp,
    datasetName,
    datasetTitle,
    editableFields,
    features,
    fieldsConditionsCount,
    hideCheckbox,
    isDatasetRoot,
    isShowForm,
    keyMap,
    labelTitle,
    locale,
    openMode,
    recordViewMap,
    rowIdField,
    rows,
    selectedIds,
    totals = 0,
    withBorders = theme.sizes.gridBorderSide as TBorderSide[],
    onActiveChange,
    onChange,
    onChanged,
    onChangeSelectedIds,
    onCloseForm,
    onError,
    onInitiateCommand,
    onInitiateCommandsScheduled,
    onMoveColumn,
    onResizeColumn,
    onSortChange,
    onUpdateConfig,
  } = props;
  const classes = useStyles();

  const [activeRow, setActiveRow] = useState(0);
  const [activeCell, setActiveCell] = useState(0);
  const [isShowCells, setIsShowCells] = useState(true);
  const [visibleRows, setVisibleRows] = useState(0);
  const [orderedColumns, setOrderedColumns] = useState<DatasetGridColumn[]>([]);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [shouldDisableScroll, setShouldDisableScroll] = useState(true);
  const [mode, setMode] = useState<GRID_MODE>(GRID_MODE.VIEW);
  const [clickedCell, setClickedCell] = useState<
    [row: number, column: number] | undefined
  >(undefined);
  const [isRunCommand, setRunCommand] = useState(false);
  const [exportAction, setExportAction] = useState<'csv' | 'excel' | null>(
    null
  );
  const [isCopyToastOpen, setCopyToastOpen] = useState(false);
  const exportMounted = useRef(false);
  if (exportAction !== null) exportMounted.current = true;

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const gridRef = useRef<MultiGrid | null>(null);
  const searchAnchorRef = useRef<HTMLDivElement | null>(null);
  const [searchInputPosition, setSearchInputPosition] = useState({
    left: 0,
    width: SEARCH_INPUT_MIN_WIDTH as number,
  });
  const isInternalActiveRowChangeRef = useRef(false);
  const lastScrolledColumnRef = useRef<number | undefined>(undefined);

  const getGridScrollElement = useCallback(
    () =>
      wrapperRef.current?.querySelector<HTMLElement>(
        '.grid__bottom-right.ReactVirtualized__Grid'
      ) ?? null,
    []
  );

  const datasetId = useRef<string>(datasetIdProp);

  const filteredFeatures = useMemo(
    () => features.filter(e => e !== FEATURES.CHECKBOX || !hideCheckbox),
    [features, hideCheckbox]
  );

  const contextMenu = useContextMenu();

  const intlFormatter = useCallback<IntlFormatter>(
    (format, value, domain, useGrouping) =>
      getFormattedValue(intl, format, value, domain, useGrouping),
    [intl]
  );

  const handleCopy = useCallback(async (): Promise<void> => {
    await copyTextToClipboard(
      getFormattedActiveCellValue(
        orderedColumns,
        activeCell,
        rows,
        activeRow,
        intlFormatter
      )
    );
    return setCopyToastOpen(true);
  }, [orderedColumns, activeCell, rows, activeRow, intlFormatter]);

  const setActiveRowAndScroll = useCallback((index: number) => {
    setShouldDisableScroll(false);
    setActiveRow(index);
  }, []);

  const setInternalActiveRow = useCallback((index: number) => {
    isInternalActiveRowChangeRef.current = true;
    setActiveRow(index);
  }, []);

  const setActiveCellAndScroll = useCallback((index: number) => {
    setShouldDisableScroll(false);
    setActiveCell(index);
  }, []);

  const onHeaderDragStart = useHeaderDragAutoScroll({
    getScrollElement: getGridScrollElement,
    onDragStartSetActiveCell: setActiveCellAndScroll,
  });

  const rowIdFieldChecksum = useMemo(
    () =>
      rows.reduce(
        (sum, row, index) => sum + row[rowIdField] * (index + 1) * 31,
        0
      ) +
      rows.length * 1009,
    [rows, rowIdField]
  );

  useEffect(() => {
    if (activeRowId) {
      rows.find((row, index) =>
        row[rowIdField] === activeRowId ? setActiveRowAndScroll(index) : false
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowIdFieldChecksum, activeRowId]);

  const onActiveChangeDebounce = useMemo(
    () => debounce(onActiveChange, 300),
    [onActiveChange]
  );

  const gridFocus = useCallback(() => {
    document.getElementById(DATASET_ID_PREFIX + datasetId.current)?.focus();
  }, []);

  useEffect(() => {
    if (mode === GRID_MODE.VIEW) gridFocus();
  }, [gridFocus, mode]);

  const closeFilterPopover = useCallback(() => {
    setIsPopoverOpen(false);
    gridFocus();
  }, [gridFocus]);

  useEffect(() => {
    if (rows.length && rows[activeRow]) {
      if (isInternalActiveRowChangeRef.current) {
        isInternalActiveRowChangeRef.current = false;
        return;
      }
      if (mode === GRID_MODE.EDIT) {
        onActiveChange(rows[activeRow][rowIdField]);
      } else {
        onActiveChangeDebounce(rows[activeRow][rowIdField]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRow, mode, rowIdField]);

  useEffect(() => {
    if (activeCell < 0) {
      setActiveCell(0);
    }
  }, [activeCell]);

  useEffect(() => {
    if (isRunCommand) {
      const { defaultCommand } = config.preset[config.currentPreset];

      if (defaultCommand && getCommand(defaultCommand, commands)?.available) {
        onInitiateCommand(defaultCommand);
      }
      setRunCommand(false);
    }
  }, [
    isRunCommand,
    commands,
    onInitiateCommand,
    config.preset,
    config.currentPreset,
  ]);

  const toggleCheckbox = useCallback(
    (index: number) => {
      setMode(GRID_MODE.VIEW);
      if (!rows[index]) {
        return;
      }
      setActiveRow(index);
      const filtered = selectedIds.filter(id => id !== rows[index][rowIdField]);
      if (filtered.length === selectedIds.length) {
        filtered.push(rows[index][rowIdField]);
      }
      onChangeSelectedIds(filtered);
    },
    [selectedIds, rows, rowIdField, onChangeSelectedIds]
  );

  const toggleAll = useCallback(() => {
    setMode(GRID_MODE.VIEW);
    onChangeSelectedIds(
      selectedIds.length === rows.length ? [] : rows.map(row => row[rowIdField])
    );
  }, [rows, onChangeSelectedIds, selectedIds.length, rowIdField]);

  const inverseSelection = useCallback(() => {
    setMode(GRID_MODE.VIEW);
    const prevSelectedIds = new Set(selectedIds);

    onChangeSelectedIds(
      rows.reduce((arr: number[], row) => {
        if (!prevSelectedIds.has(row[rowIdField])) {
          arr.push(row[rowIdField]);
        }
        return arr;
      }, [])
    );
  }, [rows, rowIdField, onChangeSelectedIds, selectedIds]);

  const {
    showSearchInput,
    setShowSearchInput,
    searchValue,
    searchInvalid,
    searchContains,
    searchCaseSensitive,
    searchInputRef,
    handleSearch,
    handleSearchChange,
    handleToggleContains,
    handleToggleCaseSensitive,
    handleSearchPrev,
    handleSearchNext,
    hasPrev,
    hasNext,
    currentSearchMatch,
    totalSearchMatches,
    closeSearchInput,
  } = useDatasetSearch({
    rows,
    orderedColumns,
    activeCell,
    activeRow,
    rowIdField,
    intlFormatter,
    gridFocus,
    gridRef,
    containerRef: wrapperRef,
    setActiveRow,
    setShouldDisableScroll,
  });

  const updateSearchInputPosition = useCallback(() => {
    const anchor = searchAnchorRef.current;
    const wrapper = wrapperRef.current;
    if (!anchor || !wrapper) return;

    const anchorRect = anchor.getBoundingClientRect();
    const wrapperRect = wrapper.getBoundingClientRect();
    const availableWidth = Math.max(wrapperRect.width - 2, 0);
    const width = Math.min(
      Math.max(anchorRect.width, SEARCH_INPUT_MIN_WIDTH),
      availableWidth
    );
    const maxLeft = Math.max(wrapperRect.width - width - 1, 1);

    setSearchInputPosition({
      left: Math.min(Math.max(anchorRect.left - wrapperRect.left, 1), maxLeft),
      width,
    });
  }, []);

  useEffect(() => {
    if (!showSearchInput) return;

    updateSearchInputPosition();
    const scrollElement = getGridScrollElement();
    scrollElement?.addEventListener('scroll', updateSearchInputPosition);
    window.addEventListener('resize', updateSearchInputPosition);

    return () => {
      scrollElement?.removeEventListener('scroll', updateSearchInputPosition);
      window.removeEventListener('resize', updateSearchInputPosition);
    };
  }, [
    activeCell,
    getGridScrollElement,
    showSearchInput,
    updateSearchInputPosition,
  ]);

  const onContextMenuOpen = useCallback(
    (event: React.MouseEvent) => {
      const target = event.target as HTMLElement;
      if (wrapperRef.current?.contains(target) && mode === GRID_MODE.VIEW) {
        const { x, y } = getCoordsFromElementOrEvent(
          event,
          target === wrapperRef.current
            ? target.querySelector('.grid__cell--focused')
            : undefined
        );

        contextMenu.setPositionX(x);
        contextMenu.setPositionY(y);
        contextMenu.open();
        closeFilterPopover();
      }
    },
    [mode, contextMenu, closeFilterPopover]
  );

  const handleCellClick = useCallback(
    ({
      currentTarget: {
        dataset: { selectableColumnIndex, selectableRowIndex },
      },
    }: MouseEvent<HTMLDivElement>) => {
      if (!selectableColumnIndex || !selectableRowIndex) {
        return;
      }
      setActiveRow(+selectableRowIndex);
      setActiveCell(+selectableColumnIndex);

      const { field: columnName } =
        orderedColumns[+selectableColumnIndex] ?? {};
      const editableFieldConfig = editableFields?.[columnName];

      if (editableFieldConfig) {
        setClickedCell([+selectableRowIndex, +selectableColumnIndex]);
        setMode(GRID_MODE.EDIT);
      } else {
        setClickedCell(undefined);
        setMode(GRID_MODE.VIEW);
      }
    },
    [editableFields, orderedColumns]
  );

  const handleCellMouseDown = useCallback(
    ({
      currentTarget: {
        dataset: { selectableColumnIndex, selectableRowIndex },
      },
    }: MouseEvent<HTMLDivElement>) => {
      if (!showSearchInput) return;
      if (!selectableColumnIndex || !selectableRowIndex) return;
      setActiveRow(+selectableRowIndex);
      setActiveCell(+selectableColumnIndex);
    },
    [showSearchInput]
  );

  const handleCellContextMenu = useCallback(
    ({
      currentTarget: {
        dataset: { selectableColumnIndex, selectableRowIndex },
      },
    }: MouseEvent<HTMLDivElement>) => {
      if (selectableColumnIndex && selectableRowIndex) {
        setActiveRow(+selectableRowIndex);
        setActiveCell(+selectableColumnIndex);
      }
    },
    []
  );

  const getCellWidth = useCallback((value: any, classString: string) => {
    const cell = document.createElement('div');
    cell.classList.add(classString);
    cell.appendChild(document.createTextNode(value));
    document.body.appendChild(cell);
    const { width } = cell.getBoundingClientRect();
    cell.remove();
    return width;
  }, []);

  const getAutoWidth = useCallback(
    (selectableColIndex: number) => {
      const { field, title } = orderedColumns[selectableColIndex];
      const { rules } = config.preset[config.currentPreset];
      const renamedTitle = rules[field].title?.[locale];

      const { format, formatter, domain } =
        orderedColumns[selectableColIndex] ?? {};
      const cellValue = rows.reduce((value, row) => {
        let formattedValue = formatter
          ? formatter(row[field], { row })
          : intlFormatter(format, row[field], domain);
        formattedValue = formattedValue?.toString().trim();

        return value.length < formattedValue?.length ? formattedValue : value;
      }, '');

      let cellWidth = getCellWidth(cellValue, 'hidden-cell');
      const headerCellWidth =
        getCellWidth(renamedTitle || title, 'hidden-cell-header') + 3;

      switch (format) {
        case DatasetColumnFormat.STRING:
        case DatasetColumnFormat.NUMBER:
          cellWidth += theme.sizes.input.height;
          break;
        case DatasetColumnFormat.DATE:
          cellWidth += theme.sizes.input.height * 2;
          break;
      }
      if (editableFields?.[field]?.dictionary) {
        cellWidth += theme.sizes.input.height;
      }

      return Math.max(headerCellWidth, cellWidth);
    },
    [
      orderedColumns,
      config.preset,
      config.currentPreset,
      locale,
      rows,
      getCellWidth,
      intlFormatter,
      editableFields,
      theme.sizes.input.height,
    ]
  );

  const handleSortChange = useCallback<SortChangeHandler>(
    (field, multiSort) => {
      setMode(GRID_MODE.VIEW);
      if (filteredFeatures.includes(FEATURES.SORTING)) {
        onSortChange(field, multiSort);
      }
    },
    [onSortChange, filteredFeatures]
  );

  const headerCellRenderer = useCallback(
    ({ columnIndex, key, style }: GridCellProps) => (
      <GridHeader
        key={key}
        columnIndex={columnIndex}
        conditions={conditions}
        config={config}
        editableFields={editableFields}
        features={filteredFeatures}
        locale={locale}
        orderedColumns={orderedColumns}
        rows={rows}
        selectedIds={selectedIds}
        style={style}
        onGetAutoWidth={getAutoWidth}
        onInverseSelection={inverseSelection}
        onMoveColumn={onMoveColumn}
        onResizeColumn={onResizeColumn}
        onSetActiveCell={setActiveCell}
        onSortChange={handleSortChange}
        onToggleAll={toggleAll}
        onToggleShowCells={setIsShowCells}
        onHeaderDragStart={onHeaderDragStart}
        searchAnchorRef={
          columnIndex === activeCell + 1 ? searchAnchorRef : undefined
        }
      />
    ),
    [
      activeCell,
      conditions,
      config,
      editableFields,
      filteredFeatures,
      locale,
      orderedColumns,
      rows,
      selectedIds,
      getAutoWidth,
      handleSortChange,
      inverseSelection,
      onMoveColumn,
      onResizeColumn,
      toggleAll,
      onHeaderDragStart,
      searchAnchorRef,
    ]
  );

  const getTotalsClassName = useCallback(
    (rowIndex: number) =>
      rowIndex >= rows.length - totals ? 'grid__totals' : '',
    [rows.length, totals]
  );

  const checkboxCellRenderer = useCallback(
    ({ key, style, rowIndex }: GridCellProps) => {
      const selectableRowIndex = rowIndex - 1;
      const row = rows[selectableRowIndex];

      return (
        <div key={key} style={style} className="grid__cell-checkbox">
          <div
            className={`${
              filteredFeatures.includes(FEATURES.CHECKBOX)
                ? `grid__aside-checkbox-wrapper ${getTotalsClassName(
                    selectableRowIndex
                  )}`
                : 'grid__aside-label-wrapper'
            }`}
          >
            {filteredFeatures.includes(FEATURES.CHECKBOX) &&
              rowIndex <= rows.length - totals && (
                <Checkbox
                  name={rowIndex}
                  value={selectedIds.includes(row[rowIdField])}
                  size={theme?.sizes.gridCheckbox.size}
                  onChange={() => toggleCheckbox(selectableRowIndex)}
                />
              )}
            {filteredFeatures.includes(FEATURES.LABEL) && labelTitle && (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
              <div
                className={getTotalsClassName(selectableRowIndex)}
                onClick={() => setActiveRow(selectableRowIndex)}
              >
                {'$ROW_HIDDEN_LABEL' in row && row['$ROW_HIDDEN_LABEL']}
              </div>
            )}
          </div>
        </div>
      );
    },
    [
      filteredFeatures,
      labelTitle,
      rowIdField,
      rows,
      selectedIds,
      theme,
      totals,
      getTotalsClassName,
      toggleCheckbox,
    ]
  );

  const handleCellDoubleClick = useCallback(
    ({
      currentTarget: {
        dataset: { selectableColumnIndex, selectableRowIndex },
      },
    }: MouseEvent<HTMLDivElement>) => {
      if (!selectableColumnIndex || !selectableRowIndex) {
        return;
      }
      setActiveCell(+selectableColumnIndex);

      if (rows[+selectableRowIndex][rowIdField] === activeRowId) {
        setRunCommand(true);
      } else {
        setActiveRow(+selectableRowIndex);
        const { defaultCommand } = config.preset[config.currentPreset];
        if (defaultCommand) {
          onInitiateCommandsScheduled([defaultCommand]);
        }
      }
    },
    [
      rows,
      rowIdField,
      activeRowId,
      config.preset,
      config.currentPreset,
      onInitiateCommandsScheduled,
    ]
  );

  const handleCellSubmit: CellInputSubmitHandler = useCallback(
    (...args) => {
      if (args[0] === GRID_MODE.EDIT) {
        const [, submitDirection] = args;
        if (submitDirection) {
          setClickedCell(oldCoordinates => {
            if (oldCoordinates) {
              const [nextRowIndex, nextColIndex] = getNextCellIndex(
                oldCoordinates,
                submitDirection
              );
              const isNextRowExist =
                nextRowIndex >= 0 && nextRowIndex < rows.length;
              const isNextColExist =
                nextColIndex >= 0 && nextColIndex < orderedColumns.length;
              if (
                isNextRowExist &&
                isNextColExist &&
                editableFields?.[orderedColumns[nextColIndex].field]
              ) {
                if (oldCoordinates[1] !== nextColIndex) {
                  setActiveCellAndScroll(nextColIndex);
                }

                if (nextRowIndex !== oldCoordinates[0]) {
                  setActiveRow(nextRowIndex);
                }

                return [nextRowIndex, nextColIndex];
              } else {
                setMode(GRID_MODE.VIEW);
              }
            }
          });
        }
      } else {
        setMode(GRID_MODE.VIEW);
      }
    },
    [rows.length, orderedColumns, editableFields, setActiveCellAndScroll]
  );

  const isDraggable = useMemo(
    () => Boolean(getCommand('move', commands)),
    [commands]
  );

  const handleDragStart = useCallback(
    (event: DragEvent) => {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer?.setData(
        'DataTree',
        JSON.stringify({
          id: JSON.stringify({
            ids: selectedIds.length
              ? selectedIds
              : [
                  rows[
                    Number((event.currentTarget as HTMLElement)?.dataset.row)
                  ][rowIdField],
                ],
          }),
        })
      );
    },
    [rowIdField, rows, selectedIds]
  );

  const cellRenderer = useCallback(
    (gridCellProps: GridCellProps) => {
      const { columnIndex, key, rowIndex, style } = gridCellProps;
      if (rowIndex === 0) {
        return headerCellRenderer(gridCellProps);
      } else {
        const { rules, hideZeroValues } = config.preset[config.currentPreset];
        const selectableColIndex = columnIndex - 1;
        const selectableRowIndex = rowIndex - 1;
        const isRowActive = activeRow === selectableRowIndex;
        const activeClass = isRowActive ? 'grid__cell--active' : '';
        const {
          field: columnName,
          format,
          formatter,
          domain,
        } = orderedColumns[selectableColIndex] ?? {};
        const row = rows[selectableRowIndex];
        const cell = row[columnName];
        const rowStyles = getRowStyles(rules, row, columns);
        const value = formatter
          ? formatter(cell, { row })
          : intlFormatter(format, cell, domain);
        if (columnIndex === 0) {
          return filteredFeatures.includes(FEATURES.CHECKBOX) ||
            (filteredFeatures.includes(FEATURES.LABEL) && labelTitle)
            ? checkboxCellRenderer(gridCellProps)
            : null;
        } else {
          const cellStyles = getCellStyles(format, rules[columnName], cell);
          const focusedClass =
            isRowActive && activeCell + 1 === columnIndex
              ? 'grid__cell--focused'
              : '';
          const textRightClass =
            domain.type === 'number' ? 'grid__cell--text-right' : '';
          const isHideZero =
            domain.type === 'number' && cell === 0 && hideZeroValues;

          const [editableRowIndex, editableColIndex] = clickedCell ?? [];

          if (
            mode === GRID_MODE.EDIT &&
            editableRowIndex === selectableRowIndex &&
            editableColIndex === selectableColIndex &&
            rowIndex <= rows.length - totals
          ) {
            return (
              <CellInput
                className={activeClass}
                columnIndex={editableColIndex}
                columnName={columnName}
                datasetId={datasetIdProp}
                domain={domain}
                editingConfig={editableFields?.[columnName]}
                key={key}
                row={rows[editableRowIndex]}
                rowIdField={rowIdField}
                rows={rows}
                style={style}
                value={cell}
                onSubmit={handleCellSubmit}
                onUpdateRows={callInSequence(onChange, onChanged)}
              />
            );
          }

          return (
            <div
              key={key}
              style={{
                ...style,
                ...applyOddEvenRowStyles(rowStyles, rowIndex),
              }}
              data-row={selectableRowIndex}
              data-cell={selectableColIndex}
              id={`${CELL_ID_PREFIX}${selectableRowIndex}${selectableColIndex}`}
              className={`${
                getTotalsClassName(selectableRowIndex) ? '' : activeClass
              } ${focusedClass} grid__cell--${
                isEven(rowIndex) ? 'even' : 'odd'
              } grid__cell`}
              draggable={isDraggable}
              onDragStart={handleDragStart}
            >
              {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
              <div
                className={`${
                  getTotalsClassName(selectableRowIndex) ? '' : activeClass
                } grid__cell-value ${textRightClass} ${getTotalsClassName(
                  selectableRowIndex
                )}`}
                data-selectable-column-index={selectableColIndex}
                data-selectable-row-index={selectableRowIndex}
                style={cellStyles}
                title={value}
                onMouseDown={handleCellMouseDown}
                onClick={handleCellClick}
                onContextMenu={handleCellContextMenu}
                onDoubleClick={handleCellDoubleClick}
              >
                {!isHideZero &&
                  (showSearchInput &&
                  searchValue &&
                  selectableColIndex === activeCell ? (
                    <SearchHighlighter
                      isNumber={domain.type === 'number'}
                      searchValue={searchValue}
                      searchContains={searchContains}
                      searchCaseSensitive={searchCaseSensitive}
                      textToHighlight={String(value ?? '')}
                    />
                  ) : (
                    value
                  ))}
              </div>
            </div>
          );
        }
      }
    },
    [
      headerCellRenderer,
      config.preset,
      config.currentPreset,
      activeRow,
      orderedColumns,
      rows,
      columns,
      intlFormatter,
      filteredFeatures,
      labelTitle,
      checkboxCellRenderer,
      activeCell,
      clickedCell,
      mode,
      totals,
      getTotalsClassName,
      isDraggable,
      handleDragStart,
      handleCellClick,
      handleCellMouseDown,
      handleCellContextMenu,
      handleCellDoubleClick,
      showSearchInput,
      searchValue,
      searchContains,
      searchCaseSensitive,
      datasetIdProp,
      editableFields,
      rowIdField,
      handleCellSubmit,
      onChange,
      onChanged,
    ]
  );

  const columnWidth = useCallback<
    Exclude<MultiGridProps['columnWidth'], number>
  >(
    ({ index }) => {
      if (index === 0) {
        const checkboxColumnWidth = filteredFeatures.includes(FEATURES.CHECKBOX)
          ? CHECKBOX_COLUMN_WIDTH
          : 0;
        if (filteredFeatures.includes(FEATURES.LABEL) && labelTitle) {
          let labelLength = 0;
          const rowIdWithLongestLabel = rows.reduce((acc, row, idx) => {
            if (row[$ROW_HIDDEN_LABEL].length > labelLength) {
              labelLength = row[$ROW_HIDDEN_LABEL].length;
              acc = idx;
            }
            return acc;
          }, 0);
          const cellWidth = getCellWidth(
            rows.length ? rows[rowIdWithLongestLabel][$ROW_HIDDEN_LABEL] : rows,
            'hidden-cell'
          );
          return cellWidth > DEFAULT_COLUMN_WIDTH
            ? DEFAULT_COLUMN_WIDTH
            : cellWidth + checkboxColumnWidth;
        }
        return checkboxColumnWidth;
      } else if (orderedColumns.length) {
        const configWidth =
          config.columnsWidth[orderedColumns[index - 1]?.field];
        if (configWidth) {
          return configWidth;
        } else {
          const autoWidth = getAutoWidth(index - 1);
          return Math.min(autoWidth, DEFAULT_COLUMN_WIDTH);
        }
      } else {
        return DEFAULT_COLUMN_WIDTH;
      }
    },
    [
      config.columnsWidth,
      orderedColumns,
      filteredFeatures,
      labelTitle,
      rows,
      getAutoWidth,
      getCellWidth,
    ]
  );

  const updateVisibleRows = useCallback(() => {
    if (wrapperRef.current) {
      setVisibleRows(
        Math.floor((wrapperRef.current.offsetHeight - ROW_HEIGHT) / ROW_HEIGHT)
      );
    }
  }, []);

  useEffect(() => {
    if (!isShowCells && gridRef.current) {
      gridRef.current.recomputeGridSize();
      setIsShowCells(true);
    }
  }, [config, isShowCells]);

  useEffect(() => {
    const rules = config.preset[config.currentPreset].rules;

    setOrderedColumns(
      config.columnsOrder.reduce((ordered, field) => {
        if (rules[field].visibility) {
          ordered.push(columns.find(column => column.field === field));
        }
        return ordered;
      }, [] as any)
    );
  }, [config, columns]);

  useEffect(() => {
    gridRef.current?.recomputeGridSize();
  }, [activeCell, activeRow, selectedIds, rows, config]);

  const memoizedColumnsOrder = useMemo(
    () => getOrderedColumns(config.columnsOrder, columns),
    [columns, config.columnsOrder]
  );

  const selectedRows = useMemo(
    () =>
      selectedIds.length
        ? rows.filter(row => selectedIds.includes(row[rowIdField]))
        : [],
    [rows, rowIdField, selectedIds]
  );

  const calculateFixedColumnCount = useCallback(
    (width: number) => {
      const desired = Math.min(
        config.preset[config.currentPreset].pinned + 1,
        orderedColumns.length + 1
      );

      let fixedWidth = 0;
      for (let i = 0; i < desired; i++) {
        const w = columnWidth({ index: i });
        if (fixedWidth + w + MIN_COLUMN_WIDTH > width) return i;
        fixedWidth += w;
      }

      return desired;
    },
    [columnWidth, config.currentPreset, config.preset, orderedColumns.length]
  );

  const scrollToRow: number | undefined = useMemo(
    () => (shouldDisableScroll ? undefined : activeRow + 1),
    [shouldDisableScroll, activeRow]
  );

  const scrollToColumn: number | undefined = useMemo(() => {
    if (!shouldDisableScroll && lastScrolledColumnRef.current !== activeCell) {
      lastScrolledColumnRef.current = activeCell;
      return activeCell + 1;
    }
  }, [activeCell, shouldDisableScroll]);

  const handleDatasetGridClick = (): void => {
    setShouldDisableScroll(true);
  };

  const handleDatasetFocus = (
    event: React.FocusEvent<HTMLDivElement>
  ): void => {
    const panel = event.currentTarget.closest(
      `.${ActivePanelManager.helpers.panel}`
    );
    if (panel?.classList.contains('active')) {
      event.stopPropagation();
    }

    if (mode === GRID_MODE.EDIT) {
      const inputCellElement = document.querySelector<HTMLInputElement>(
        `.${GRID_CELL_INPUT_CLASS} input`
      );
      inputCellElement?.focus();
    }
  };

  const getWrapperRef = useCallbackRef(wrapperRef, ref);

  useKeyDown(
    wrapperRef,
    {
      activeCell,
      activeRow,
      columns,
      features: filteredFeatures,
      gridRef,
      keyMap,
      labelTitle,
      mode,
      orderedColumns,
      rowIdField,
      rows,
      showSearchInput,
      visibleRows,
      intlFormatter,
      inverseSelection,
      onActiveChange,
      onChangeSelectedIds,
      onCloseSearchInput: closeSearchInput,
      onCopySuccess: () => setCopyToastOpen(true),
      onInitiateCommand,
      onSearch: handleSearch,
      setActiveCell,
      setActiveRow,
      setInternalActiveRow,
      setIsPopoverOpen,
      setShouldDisableScroll,
      setShowSearchInput,
      toggleAll,
      toggleCheckbox,
      editableFields,
      setClickedCell,
      setMode,
    },
    filteredFeatures.includes(FEATURES.KEYDOWN)
  );

  useEffect(() => {
    if (isDatasetRoot) wrapperRef.current?.focus();
  }, [isDatasetRoot]);

  useEffect(() => {
    if (isDatasetRoot && activeCategoryId === categoryId)
      wrapperRef.current?.focus();
  }, [activeCategoryId, categoryId, isDatasetRoot]);

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events
    <div
      className={classNames(
        ActivePanelManager.helpers.target,
        'grid__wrapper',
        classes.agThemas,
        getClassNamesFromMap(
          classes,
          getBorderClassNames('gridBorder', withBorders)
        ),
        className
      )}
      id={DATASET_ID_PREFIX + datasetId.current}
      tabIndex={0} // eslint-disable-line jsx-a11y/no-noninteractive-tabindex
      ref={getWrapperRef}
      onContextMenu={onContextMenuOpen}
      onClick={handleDatasetGridClick}
      onFocus={handleDatasetFocus}
    >
      <AutoSizer className="grid__auto-sizer">
        {({ height, width }) => (
          <MultiGrid
            classNameBottomLeftGrid="grid__bottom-left"
            classNameBottomRightGrid="grid__bottom-right"
            classNameTopLeftGrid="grid__top-left"
            classNameTopRightGrid="grid__top-right"
            cellRenderer={cellRenderer}
            columnCount={orderedColumns.length + 1}
            scrollToRow={scrollToRow}
            scrollToColumn={scrollToColumn}
            columnWidth={columnWidth}
            rowCount={rows.length + 1}
            width={width}
            height={height}
            rowHeight={ROW_HEIGHT}
            fixedRowCount={1}
            fixedColumnCount={calculateFixedColumnCount(width)}
            ref={gridRef}
            onSectionRendered={updateVisibleRows}
            mode={mode}
            clickedCell={clickedCell}
          />
        )}
      </AutoSizer>
      {showSearchInput && filteredFeatures.includes(FEATURES.FIND_INPUT) && (
        <SearchInput
          ref={searchInputRef}
          value={searchValue}
          width={searchInputPosition.width}
          position={{ left: searchInputPosition.left }}
          invalid={searchInvalid}
          currentSearchMatch={currentSearchMatch}
          totalSearchMatches={totalSearchMatches}
          searchContains={searchContains}
          searchCaseSensitive={searchCaseSensitive}
          onCloseSearchInput={closeSearchInput}
          onSearch={handleSearch}
          onSearchChange={handleSearchChange}
          onToggleContains={handleToggleContains}
          onToggleCaseSensitive={handleToggleCaseSensitive}
          onSearchPrev={handleSearchPrev}
          onSearchNext={handleSearchNext}
          hasPrev={hasPrev}
          hasNext={hasNext}
        />
      )}
      {!!orderedColumns.length && isPopoverOpen && (
        <FilterPopover
          open={true}
          config={config}
          datasetId={datasetIdProp}
          activeCell={activeCell}
          activeRow={activeRow}
          locale={locale}
          orderedColumns={orderedColumns}
          rows={rows}
          categoryId={categoryId}
          datasetName={datasetName}
          conditions={conditions}
          closePopover={closeFilterPopover}
        />
      )}
      {filteredFeatures.includes(FEATURES.CONTEXT_MENU) && commands && (
        <GridContextMenu
          categoryId={categoryId}
          commands={commands}
          conditionsCount={conditionsCount}
          config={config}
          contextMenu={contextMenu}
          datasetId={datasetIdProp}
          datasetName={datasetName}
          fieldsConditionsCount={fieldsConditionsCount}
          gridFocus={gridFocus}
          onCopy={handleCopy}
          onInitiateCommand={onInitiateCommand}
          onSetIsPopoverOpen={setIsPopoverOpen}
          onExportCSV={() => setExportAction('csv')}
          onExportExcel={() => setExportAction('excel')}
        />
      )}
      {exportMounted.current && (
        <DatasetExportController
          datasetId={datasetIdProp}
          orderedColumns={orderedColumns}
          activeRowIndex={activeRow}
          selectedRows={selectedRows}
          allRows={rows}
          rules={config.preset[config.currentPreset].rules}
          locale={locale}
          datasetTitle={datasetTitle}
          convert={intlFormatter}
          pendingAction={exportAction}
          onActionHandled={() => setExportAction(null)}
        />
      )}
      {isShowForm && commands && (
        <DatasetConfigForm
          orderedColumns={memoizedColumnsOrder}
          key={datasetIdProp}
          openMode={openMode}
          hasConditionsHook={hasConditionsHook}
          title={intl.formatMessage(messages.datasetConfigFormTitle)}
          open={isShowForm}
          onCloseForm={onCloseForm}
          onUpdateConfig={onUpdateConfig}
          config={config}
          locale={locale}
          commands={commands}
          onError={onError}
          columns={columns}
          recordViewMap={recordViewMap}
        />
      )}
      <Toasts open={isCopyToastOpen}>
        <Toasts.Toast
          autoDismissTimeOut={1500}
          className={classes.toast}
          type="success"
          onClose={() => setCopyToastOpen(false)}
        >
          {intl.formatMessage(messages.copied)}
        </Toasts.Toast>
      </Toasts>
    </div>
  );
};

export default memo(forwardRef(DatasetGrid));
